import React, { useState, useCallback, useEffect } from "react";
import ReactGA from 'react-ga4';
import "./App.css";

// Components
import SEO from './components/SEO';
import Header from "./components/Header";
import HeroSection from "./components/HeroSection";
import Services from "./components/Services";
import GallerySection from "./components/GallerySection";
import VideoGallerySection from "./components/VideoGallerySection";
import Footer from "./components/Footer";
import ContactModal from "./components/ContactModal";

// Safe GA tracking function
const trackEvent = (category, action, label) => {
  try {
    if (window.ReactGA && process.env.NODE_ENV === 'production') {
      ReactGA.event({
        category,
        action,
        label
      });
    }
  } catch (error) {
    console.warn('Analytics error:', error);
  }
};

function App() {
  const [isContactOpen, setIsContactOpen] = useState(false);

  // Initialize GA on mount only in production
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      try {
        ReactGA.initialize('G-XXXXXXXX'); // Replace with your actual GA4 ID
        ReactGA.send('pageview');
      } catch (error) {
        console.warn('Analytics initialization error:', error);
      }
    }
  }, []);

  // Track scroll depth
  useEffect(() => {
    let scrollDepths = new Set();
    const handleScroll = () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );
      
      [25, 50, 75, 100].forEach(depth => {
        if (scrollPercent >= depth && !scrollDepths.has(depth)) {
          scrollDepths.add(depth);
          trackEvent('Scroll', 'Depth', `${depth}%`);
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Modal handlers
  const handleOpenContact = useCallback(() => {
    setIsContactOpen(true);
    document.body.style.overflow = 'hidden';
    trackEvent('Contact', 'Open Modal');
  }, []);

  const handleCloseContact = useCallback(() => {
    setIsContactOpen(false);
    document.body.style.overflow = 'unset';
  }, []);

  const handleContactSubmit = useCallback((formData) => {
    trackEvent('Contact', 'Form Submit', formData.service || 'General');
  }, []);

  // Scroll to top functionality
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    trackEvent('Navigation', 'Scroll to Top');
  }, []);

  return (
    <>
      <SEO />
      <div className="gradient-background">
        <div className="overlay-container">
          <Header onOpenContact={handleOpenContact} />

          <main className="main-content">
            <HeroSection onOpenContact={handleOpenContact} />
            <Services 
              onOpenContact={handleOpenContact}
              onServiceClick={(service) => trackEvent('Service', 'Click', service)}
            />
            <GallerySection 
              onImageView={(imageId) => trackEvent('Gallery', 'View Image', imageId)}
            />
            <VideoGallerySection 
              onVideoView={(videoId) => trackEvent('Gallery', 'View Video', videoId)}
            />
          </main>

          <Footer onOpenContact={handleOpenContact} />

          <ContactModal
            isOpen={isContactOpen}
            onClose={handleCloseContact}
            onSubmit={handleContactSubmit}
          />

          {showScrollTop && (
            <button 
              className="scroll-to-top"
              onClick={scrollToTop}
              aria-label="Scroll to top"
            >
              <i className="fas fa-arrow-up"></i>
            </button>
          )}
        </div>
      </div>
    </>
  );
}

export default App;